import { useState, useEffect, useMemo } from 'react';
import Axios from 'axios';
import { Alert, Button, Layout, Row } from 'antd';
import { DragDropContext } from 'react-beautiful-dnd';

import { Config } from '../config_section';

import ListOfCourses from './ListOfCourses';
import SelectedCourses from './SelectedCourses';
import Statistics from './Statistics';
import AddEditCourseModalForm from '../curricula/AddEditCourseModalForm';

import { checkColumnStructure, checkCoursesStructure } from './utils';
import dndStructure from './initStructure/dndStructure';
import sampleCourses from '../curricula/initStructure/SampleCourses';

const { Content } = Layout;

const StudyProfiler = ({ rsSkillsGroup, rsSpecificKnowledge, rsSpecificSkills, rsProfiles, showMessage }) => {
  const [courses, setCourses] = useState({});
  const [columns, setColumns] = useState(dndStructure.columns);
  const [error, setError] = useState(null);
  const [draggedCourse, setDraggedCourse] = useState(null);
  const [sampleCoursesLoaded, setSampleCoursesLoaded] = useState(false);
  const [addModalData, setAddModalData] = useState(null);
  const [modalCourseId, setModalCourseId] = useState(null);
  const [semestersToShow, setSemestersToShowHook] = useState(4);
  const [analysis, setAnalysis] = useState('settings');
  const [selectedProfiles, setSelectedProfiles] = useState([]);

  const initialColumns = useMemo(() => JSON.parse(JSON.stringify(dndStructure.columns)), []);
  useEffect(() => {
    setColumns(initialColumns);
  }, [initialColumns]);

  function createCourseDnDId(name, prefix, pool) {
    let base = `${prefix}_${name.replace(/ /g, '_').toLowerCase()}`;
    let counter = 0;
    while (pool.includes(`${base}_${counter}`)) counter++;
    return `${base}_${counter}`;
  }

  function openAddModal(courseId) {
    setModalCourseId(courseId);
    setAddModalData(courseId != null ? courses[courseId] : 'new');
  }
  function closeAddModal() {
    setModalCourseId(null);
    setAddModalData(null);
  }
  const submitModal = (inputData) => {
    if (modalCourseId == null) {
      addCustomCourse(inputData);
    } else {
      updateCourse(inputData);
    }
    closeAddModal();
  };

  function addCustomCourse(input) {
    const newCourse = { ...input };
    delete newCourse.currentTopic;
    delete newCourse.currentTopicPer;
    const id = createCourseDnDId(newCourse.name, 'user', Object.keys(courses));
    newCourse.id = id;
    const pos = `${newCourse.type}-${newCourse.semester.charAt(0)}`;
    setCourses((prev) => ({ ...prev, [id]: newCourse }));
    setColumns((prev) => ({
      ...prev,
      [pos]: {
        ...prev[pos],
        courseIds: [...prev[pos].courseIds, id],
      },
    }));
  }

  function updateCourse(input) {
    if (!modalCourseId) return;
    const newCourse = { ...input };
    delete newCourse.currentTopic;
    delete newCourse.currentTopicPer;
    const id = createCourseDnDId(newCourse.name, 'user', Object.keys(courses));
    const oldId = modalCourseId;
    const oldPos = Object.keys(columns).find((key) => columns[key].courseIds.includes(oldId));
    const newPos = isNaN(oldPos.charAt(0)) ? `${newCourse.type}-${newCourse.semester.charAt(0)}` : `${oldPos.charAt(0)}-${newCourse.semester.charAt(0)}`;

    setCourses((prev) => {
      const { [oldId]: _, ...rest } = prev;
      return { ...rest, [id]: { ...newCourse, id } };
    });
    setColumns((prev) => {
      const fromIds = prev[oldPos].courseIds.filter((c) => c !== oldId);
      const toIds = newPos === oldPos ? [...fromIds, id] : [...prev[newPos].courseIds, id];
      return {
        ...prev,
        [oldPos]: { ...prev[oldPos], courseIds: fromIds },
        [newPos]: { ...prev[newPos], courseIds: toIds },
      };
    });
  }

  function removeForeverCourse({ id, position }) {
    setCourses((prev) => {
      const { [id]: _, ...rest } = prev;
      return rest;
    });
    setColumns((prev) => ({
      ...prev,
      [position]: {
        ...prev[position],
        courseIds: prev[position].courseIds.filter((c) => c !== id),
      },
    }));
  }
  function removeCourse({ id, type, semester, position }) {
    const newCols = { ...columns };
    const oldPos = position;
    const newPos = `${type}-${semester.charAt(0)}`;
    newCols[oldPos].courseIds = newCols[oldPos].courseIds.filter((cid) => cid !== id);
    newCols[newPos].courseIds.push(id);
    setColumns(newCols);
  }

  function removeCourses(list) {
    setColumns((prev) => {
      const cols = { ...prev };
      list.forEach((course) => {
        const from = cols[course.position].courseIds.filter((c) => c !== course.id);
        const toPos = `${course.type}-${course.semester.charAt(0)}`;
        cols[course.position].courseIds = from;
        cols[toPos].courseIds = [...cols[toPos].courseIds, course.id];
      });
      return cols;
    });
  }

  function importData(input) {
    try {
      const parsed = JSON.parse(input);
      if (!parsed.courses) throw new Error();
      if (!checkColumnStructure(parsed.courses, dndStructure.columns) || !checkCoursesStructure(parsed.courses)) {
        setError({ message: 'Wrong input file format!' });
        return;
      }
      parsed.courses.forEach((item) => {
        const copy = { ...item };
        const id = createCourseDnDId(copy.name, 'import', Object.keys(courses));
        copy.id = id;
        setCourses((prev) => ({ ...prev, [id]: copy }));
        setColumns((prev) => ({
          ...prev,
          [item.position]: {
            ...prev[item.position],
            courseIds: [...prev[item.position].courseIds, id],
          },
        }));
      });
    } catch {
      setError({ message: 'Sorry, something went wrong!' });
    }
  }

  function getCoursePosition(course) {
    const { type, semester } = course;
    if (type === 'voluntary' && semester === 'summer') return 'voluntary-s';
    if (type === 'voluntary' && semester === 'winter') return 'voluntary-w';
    if (type === 'mandatory' && semester === 'summer') return 'mandatory-s';
    if (type === 'mandatory' && semester === 'winter') return 'mandatory-w';
    if (type === 'custom' && semester === 'summer') return 'custom-s';
    if (type === 'custom' && semester === 'winter') return 'custom-w';
    return null;
  }

  function loadSamples() {
    if (!sampleCourses?.length) {
      setError({ message: 'There are no sample courses available!' });
      return;
    }
    sampleCourses
      .sort((a, b) => a.name.localeCompare(b.name))
      .forEach((item) => {
        const copy = { ...item };
        const id = createCourseDnDId(copy.name, 'sample', Object.keys(courses));
        copy.id = id;
        const pos = getCoursePosition(copy);
        setCourses((prev) => ({ ...prev, [id]: copy }));
        setColumns((prev) => ({
          ...prev,
          [pos]: {
            ...prev[pos],
            courseIds: [...prev[pos].courseIds, id],
          },
        }));
      });
    setSampleCoursesLoaded(true);
  }

  function setSemestersToShow(count) {
    const toHide = [];
    Object.keys(columns).forEach((position, idx) => {
      if (idx + 1 > count * 2) {
        columns[position].courseIds.forEach((cid) => toHide.push({ ...courses[cid], position }));
      }
    });
    removeCourses(toHide);
    setSemestersToShowHook(count);
  }

  function createNewCurricula(newCurricula) {
    const newColumns = JSON.parse(JSON.stringify(columns));
    const newCourses = JSON.parse(JSON.stringify(courses));
    let err = false;

    ['1-w', '1-s', '2-w', '2-s', '3-w', '3-s', '4-w', '4-s', '5-w', '5-s'].forEach((position, i) => {
      if (newCurricula.length > i) {
        newCurricula[i].forEach((course) => {
          if (courses.hasOwnProperty(course.id) === false) {
            err = true;
            return;
          }
          let oldPosition = getCoursePosition(courses[course.id]);
          let index = newColumns[oldPosition].courseIds.indexOf(course.id);
          newColumns[oldPosition].courseIds.splice(index, 1);
          newColumns[position].courseIds.push(course.id);
          newCourses[course.id].priority = course.priority;
        });
      }
    });
    if ([].concat(...newCurricula).length === 0) {
      showMessage({ type: 'error', content: 'Course combination was not found!', duration: 5 });
      return;
    }
    if (err) {
      showMessage({ type: 'error', content: 'Some courses were not processed correctly!', duration: 5 });
    }
    setColumns(newColumns);
    setCourses(newCourses);
    setSemestersToShow(newCurricula.length);
  }

  function analyzeCurricula(values) {
    values.courses = Object.values(courses);
    values.semesters = values.semesters * 2;
    values.credit_tolerance = [values.credit_tolerance * -1, values.credit_tolerance];
    if (!Array.isArray(values.profiles)) values.profiles = [values.profiles];
    setSelectedProfiles(values.profiles);
    setAnalysis('loading');
    showMessage({ type: 'loading', content: 'Loading...', key: 'updatable', duration: 0.2 });
    Axios.post(Config.server.analyzeCurricula, { values }, { headers: { 'Content-Type': 'application/json' } })
      .then((resp) => {
        showMessage({ type: 'success', content: 'Analysis done.' });
        createNewCurricula(resp.data.data);
        setAnalysis('new-curricula');
      })
      .catch(() => {
        showMessage({ type: 'error', content: 'Something went wrong!' });
        setAnalysis('settings');
      });
  }

  function goBackAnalysis() {
    setCourses((prevCourses) => {
      const clean = {};
      Object.entries(prevCourses).forEach(([id, course]) => {
        clean[id] = { ...course };
        delete clean[id].priority;
      });
      return clean;
    });
    setColumns((prevCols) => {
      const allIds = Object.keys(courses);
      const newCols = {};
      Object.entries(prevCols).forEach(([colId, col]) => {
        if (colId === 'available') {
          newCols[colId] = { ...col, courseIds: allIds };
        } else {
          newCols[colId] = { ...col, courseIds: [] };
        }
      });
      return newCols;
    });

    setAnalysis('settings');
    setSemestersToShow(0);
  }

  function onDragStart(start) {
    setDraggedCourse(start.draggableId);
  }

  function onDragEnd(result) {
    const { destination, source, draggableId } = result;
    if (!destination) {
      setDraggedCourse(null);
      return;
    }
    if (destination.droppableId === source.droppableId && destination.index === source.index) {
      setDraggedCourse(null);
      return;
    }

    const startCol = columns[source.droppableId];
    const endCol = columns[destination.droppableId];

    if (startCol === endCol) {
      const ids = Array.from(startCol.courseIds);
      ids.splice(source.index, 1);
      ids.splice(destination.index, 0, draggableId);
      const updated = { ...startCol, courseIds: ids };
      setColumns((prev) => ({
        ...prev,
        [updated.id]: updated,
      }));
    } else {
      const startIds = Array.from(startCol.courseIds);
      startIds.splice(source.index, 1);

      const finishIds = Array.from(endCol.courseIds);
      finishIds.splice(destination.index, 0, draggableId);

      setColumns((prev) => ({
        ...prev,
        [startCol.id]: { ...startCol, courseIds: startIds },
        [endCol.id]: { ...endCol, courseIds: finishIds },
      }));
    }

    setDraggedCourse(null);
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: 50 }}>
        <Alert message={error.message} type="error" showIcon />
        <Button type="link" onClick={() => setError(null)}>
          Go back
        </Button>
      </div>
    );
  }

  return (
    <Layout className="csprofiler-study">
      <Content className="structure-content">
        <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
          <Row gutter={16}>
            <ListOfCourses
              columns={columns}
              courses={courses}
              draggedCourse={draggedCourse}
              sampleCoursesLoaded={sampleCoursesLoaded}
              addCustomCourse={addCustomCourse}
              removeCourse={removeForeverCourse}
              loadSamples={loadSamples}
              openAddModal={openAddModal}
              rsSkillsGroup={rsSkillsGroup}
              importData={importData}
            />
            <SelectedCourses
              columns={columns}
              courses={courses}
              draggedCourse={draggedCourse}
              removeCourse={removeCourse}
              openAddModal={openAddModal}
              rsSkillsGroup={rsSkillsGroup}
              rsProfiles={rsProfiles}
              showMessage={showMessage}
              analyzeCurricula={analyzeCurricula}
              goBackAnalysis={goBackAnalysis}
              analysis={analysis}
              semestersToShow={semestersToShow}
              setSemestersToShow={setSemestersToShow}
            />
            <Statistics columns={columns} courses={courses} rsSkillsGroup={rsSkillsGroup} rsProfiles={rsProfiles} semestersToShow={semestersToShow} selectedProfiles={selectedProfiles} />
          </Row>
        </DragDropContext>
      </Content>

      <AddEditCourseModalForm
        editRecord={addModalData}
        saveLocally={submitModal}
        onCancel={closeAddModal}
        refresh={closeAddModal}
        showMessage={showMessage}
        rsSkillsGroup={rsSkillsGroup}
        rsSpecificKnowledge={rsSpecificKnowledge}
        rsSpecificSkills={rsSpecificSkills}
      />
    </Layout>
  );
};

export default StudyProfiler;
